import { useLayoutEffect, useMemo, useRef } from "react"

import debounce, { DEFAULT_DEBOUNCE_TIME } from "@utils/debounce"

/**
 * Debounce `callback` once per mount, always calling its latest version.
 *
 * A debounced function built during render is replaced on every render, so a call still waiting from before a
 * re-render fires alongside the next one. This one keeps its window across renders.
 */
export default function useDebouncedCallback<Args extends unknown[]>(
    callback: ((...args: Args) => void) | undefined,
    wait = DEFAULT_DEBOUNCE_TIME,
) {
    const callbackRef = useRef(callback)

    useLayoutEffect(() => {
        callbackRef.current = callback
    })

    return useMemo(() => debounce((...args: Args) => callbackRef.current?.(...args), wait), [wait])
}
